import { useState, useRef } from 'react';
import imagesService from '../../services/images.service';
import cakeService from '../../services/cakeorders.service';
import styles from './ImageReadyModal.module.css';

/**
 * ImageReadyModal - Modal for uploading created cake image or marking as no image
 *
 * @param {Object} props
 * @param {Object} props.order - The cake order object
 * @param {Function} props.onClose - Callback when modal is closed
 * @param {Function} props.onSuccess - Callback when image is uploaded or marked as no image
 */
export default function ImageReadyModal({ order, onClose, onSuccess }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB

  const handleFileSelect = (file) => {
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Por favor selecciona un archivo de imagen válido');
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      setError('La imagen es demasiado grande. El tamaño máximo es 2MB.');
      return;
    }

    setError(null);
    setSelectedFile(file);

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreview(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleInputChange = (e) => {
    const file = e.target.files?.[0];
    handleFileSelect(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    handleFileSelect(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    setError(null);

    try {
      // Upload image
      const { imageUrl } = await imagesService.uploadImage(selectedFile, order.id, 'cake');

      // Update cake order: set imageReady, imageNeeded, and createdImageUrl
      await cakeService.editCakeOrder(order.id, {
        imageReady: true,
        imageNeeded: true,
        createdImageUrl: imageUrl
      });

      onSuccess?.();
      onClose();
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message || 'Error al subir la imagen');
    } finally {
      setUploading(false);
    }
  };

  const handleNoImage = async () => {
    setUploading(true);
    setError(null);

    try {
      // Just mark as ready without image
      await cakeService.editCakeOrder(order.id, {
        imageReady: true,
        createdImageUrl: null
      });

      onSuccess?.();
      onClose();
    } catch (err) {
      console.error('Error marking no image:', err);
      setError(err.message || 'Error al actualizar el pedido');
    } finally {
      setUploading(false);
    }
  };

  const handleNoImageNeeded = async () => {
    setUploading(true);
    setError(null);

    try {
      await cakeService.editCakeOrder(order.id, {
        imageNeeded: false,
        imageReady: false,
        createdImageUrl: null
      });

      onSuccess?.();
      onClose();
    } catch (err) {
      console.error('Error marking no image needed:', err);
      setError(err.message || 'Error al actualizar el pedido');
    } finally {
      setUploading(false);
    }
  };

  const handleClearSelection = () => {
    setSelectedFile(null);
    setPreview(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className={styles['image-ready-modal-overlay']} onClick={onClose}>
      <div className={styles['image-ready-modal']} onClick={(e) => e.stopPropagation()}>
        <div className={styles['image-ready-modal-header']}>
          <h3>Imagen Creada</h3>
          <button className={styles['image-ready-modal-close']} onClick={onClose}>
            &times;
          </button>
        </div>

        <div className={styles['image-ready-modal-body']}>
          <p className={styles['image-ready-modal-order-info']}>
            <strong>{order.firstName} {order.lastName}</strong> - {order.cakeSize}
            {order.image && <span className={styles['image-ready-modal-description']}> ({order.image})</span>}
          </p>

          {order.imageUrl && (
            <div className={styles['image-ready-modal-reference']}>
              <p className={styles['image-ready-modal-reference-label']}>Imagen del cliente:</p>
              <img
                src={order.imageUrl}
                alt="Imagen de referencia"
                className={styles['image-ready-modal-reference-img']}
              />
            </div>
          )}

          {error && (
            <div className={styles['image-ready-modal-error']}>
              {error}
            </div>
          )}

          {!preview ? (
            <div
              className={`${styles['image-ready-modal-dropzone']} ${dragOver ? styles['dragover'] : ''}`}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleInputChange}
                className={styles['image-ready-modal-file-input']}
              />
              <div className={styles['image-ready-modal-dropzone-content']}>
                <span className={styles['image-ready-modal-dropzone-icon']}>📷</span>
                <p>Arrastra una imagen aquí o haz clic para seleccionar</p>
                <span className={styles['image-ready-modal-dropzone-hint']}>
                  JPG, PNG - Máximo 2MB
                </span>
              </div>
            </div>
          ) : (
            <div className={styles['image-ready-modal-preview-container']}>
              <img
                src={preview}
                alt="Preview"
                className={styles['image-ready-modal-preview']}
              />
              <button
                className={styles['image-ready-modal-clear-btn']}
                onClick={handleClearSelection}
                disabled={uploading}
              >
                Cambiar imagen
              </button>
            </div>
          )}
        </div>

        <div className={styles['image-ready-modal-footer']}>
          <button
            className={`${styles['image-ready-modal-btn']} ${styles['image-ready-modal-btn-tertiary']}`}
            onClick={handleNoImageNeeded}
            disabled={uploading}
          >
            {uploading ? 'Procesando...' : 'No necesita imagen'}
          </button>
          <div className={styles['image-ready-modal-footer-right']}>
            <button
              className={`${styles['image-ready-modal-btn']} ${styles['image-ready-modal-btn-secondary']}`}
              onClick={handleNoImage}
              disabled={uploading}
            >
              {uploading ? 'Procesando...' : 'Sin imagen'}
            </button>
            <button
              className={`${styles['image-ready-modal-btn']} ${styles['image-ready-modal-btn-primary']}`}
              onClick={handleUpload}
              disabled={!selectedFile || uploading}
            >
              {uploading ? 'Subiendo...' : 'Subir imagen'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
